<template>
  <div class="gl-stock-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Stock</span>
          </div>
          <h1 class="gl-branch-title">Stock Levels</h1>
        </div>
        <div class="gl-header-actions" v-if="selectedBranchId">
          <router-link :to="`/owner/branches/${selectedBranchId}/stock/movements`" class="gl-outline-btn gl-history-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            <span class="gl-history-label">History</span>
          </router-link>
          <button class="gl-add-btn gl-header-add-btn" @click="showAddModal()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Stock
          </button>
        </div>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Filter bar -->
      <div class="gl-filter-bar">
        <div class="gl-branch-select-wrap">
          <svg class="gl-branch-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          <select class="gl-branch-select" v-model="selectedBranchId" @change="onBranchChange">
            <option value="">Select a branch…</option>
            <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
          </select>
          <svg class="gl-branch-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
        <template v-if="selectedBranchId">
          <div class="gl-search-wrap">
            <svg class="gl-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" class="gl-search-input" v-model="filters.search" placeholder="Search products…" @input="debouncedSearch">
          </div>
          <select class="gl-filter-select" v-model="filters.stock_status" @change="fetchStock">
            <option value="">All Stock</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
          </select>
          <button class="gl-filter-clear" @click="resetFilters" v-if="filters.search || filters.stock_status">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            Clear
          </button>
        </template>
      </div>

      <!-- Stats strip -->
      <div class="gl-stats-strip">
        <div class="gl-stat-chip">
          <span class="gl-stat-num">{{ summary ? summary.total_products : '—' }}</span>
          <span class="gl-stat-lbl">Total Products</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-warn">{{ summary ? summary.low_stock : '—' }}</span>
          <span class="gl-stat-lbl">Low Stock</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-danger">{{ summary ? summary.out_of_stock : '—' }}</span>
          <span class="gl-stat-lbl">Out of Stock</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-info">{{ summary ? 'RM ' + formatPrice(summary.total_value) : '—' }}</span>
          <span class="gl-stat-lbl">Total Value</span>
        </div>
      </div>

      <!-- No branch selected -->
      <div v-if="!selectedBranchId" class="gl-empty">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/></svg>
        <p>Select a branch above to view stock levels</p>
      </div>

      <!-- Loading -->
      <div v-else-if="loading" class="gl-loading">
        <div class="gl-spinner"></div>
        <span>Loading stock…</span>
      </div>

      <!-- Stock table -->
      <div v-else class="gl-table-card">

        <div v-if="!products.length" class="gl-empty" style="padding: 60px 0;">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
          <p>No products found in this branch.</p>
        </div>

        <div v-else class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th class="th-center">Current Stock</th>
                <th class="th-center">Min Stock</th>
                <th>Status</th>
                <th class="th-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="product in products"
                :key="product.id"
                :class="{ 'gl-row-out': isOutOfStock(product), 'gl-row-low': !isOutOfStock(product) && isLowStock(product) }"
              >
                <td>
                  <div class="gl-prod-cell">
                    <div class="gl-prod-img-sm" :class="{ 'gl-prod-placeholder': !(product.image && !imgErrors[product.id]) }">
                      <img v-if="product.image && !imgErrors[product.id]" :src="product.image" @error="imgErrors[product.id] = true" alt="">
                      <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                    </div>
                    <div>
                      <div class="gl-prod-name">{{ product.name }}</div>
                      <div class="gl-prod-sku">{{ product.sku || 'No SKU' }}</div>
                    </div>
                  </div>
                </td>
                <td><span class="gl-cat-pill">{{ product.category?.name || '—' }}</span></td>
                <td class="td-center">
                  <span class="gl-stock-badge" :class="getStockBadgeClass(product)">{{ product.pivot?.stock || 0 }}</span>
                </td>
                <td class="td-center">
                  <span class="gl-min-stock">{{ product.pivot?.min_stock || 0 }}</span>
                </td>
                <td>
                  <span class="gl-status-pill" :class="getStatusClass(product)">{{ getStatusLabel(product) }}</span>
                </td>
                <td class="td-center">
                  <div class="gl-action-group">
                    <button class="gl-act-btn gl-act-add" @click="showAddModal(product)" title="Add Stock">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                    <button class="gl-act-btn gl-act-remove" @click="showRemoveModal(product)" title="Remove Stock">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                    <button class="gl-act-btn gl-act-adjust" @click="showAdjustModal(product)" title="Adjust Stock">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button class="gl-act-btn gl-act-alert" @click="showAlertSettings(product)" title="Alert Settings">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.last_page > 1" class="gl-pagination">
          <button class="gl-page-btn" :disabled="pagination.current_page === 1" @click="fetchStock(pagination.current_page - 1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button v-for="page in pagination.last_page" :key="page" class="gl-page-btn" :class="{ active: page === pagination.current_page }" @click="fetchStock(page)">{{ page }}</button>
          <button class="gl-page-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchStock(pagination.current_page + 1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      </div>

    </div>

    <!-- ── ADD STOCK MODAL ── -->
    <div v-if="addModalOpen" class="gl-modal-backdrop" @click.self="addModalOpen = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <div class="gl-modal-title-wrap">
            <div class="gl-modal-icon gl-modal-icon-add">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </div>
            <span class="gl-modal-title">Add Stock</span>
          </div>
          <button class="gl-modal-close" @click="addModalOpen = false">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-form-group">
            <label class="gl-label">Product</label>
            <select class="gl-select" v-model="stockForm.product_id" :disabled="!!selectedProduct">
              <option value="">Select Product</option>
              <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div class="gl-form-row">
            <div class="gl-form-group">
              <label class="gl-label">Quantity <span class="gl-required">*</span></label>
              <input type="number" class="gl-input" v-model="stockForm.quantity" min="1">
            </div>
            <div class="gl-form-group">
              <label class="gl-label">Type</label>
              <select class="gl-select" v-model="stockForm.type">
                <option value="purchase">Purchase</option>
                <option value="initial">Initial Stock</option>
              </select>
            </div>
          </div>
          <div class="gl-form-row">
            <div class="gl-form-group">
              <label class="gl-label">Unit Cost (RM)</label>
              <input type="number" class="gl-input" v-model="stockForm.unit_cost" step="0.01" min="0" placeholder="0.00">
            </div>
            <div class="gl-form-group">
              <label class="gl-label">Reference No.</label>
              <input type="text" class="gl-input" v-model="stockForm.reference_number" placeholder="PO / Invoice no.">
            </div>
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Notes</label>
            <textarea class="gl-textarea" v-model="stockForm.notes" rows="2" placeholder="Optional notes…"></textarea>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-secondary" @click="addModalOpen = false">Cancel</button>
          <button class="gl-btn-success" @click="addStock" :disabled="submitting">
            <span v-if="submitting" class="gl-btn-spinner"></span>
            Add Stock
          </button>
        </div>
      </div>
    </div>

    <!-- ── REMOVE STOCK MODAL ── -->
    <div v-if="removeModalOpen" class="gl-modal-backdrop" @click.self="removeModalOpen = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <div class="gl-modal-title-wrap">
            <div class="gl-modal-icon gl-modal-icon-remove">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </div>
            <span class="gl-modal-title">Remove Stock</span>
          </div>
          <button class="gl-modal-close" @click="removeModalOpen = false">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-info-row">
            <div class="gl-info-product">{{ selectedProduct?.name }}</div>
            <div class="gl-info-meta">Current stock: <strong>{{ selectedProduct?.pivot?.stock || 0 }}</strong></div>
          </div>
          <div class="gl-form-row">
            <div class="gl-form-group">
              <label class="gl-label">Qty to Remove <span class="gl-required">*</span></label>
              <input type="number" class="gl-input" v-model="removeForm.quantity" min="1" :max="selectedProduct?.pivot?.stock || 0">
            </div>
            <div class="gl-form-group">
              <label class="gl-label">Reason <span class="gl-required">*</span></label>
              <select class="gl-select" v-model="removeForm.type">
                <option value="waste">Waste</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Notes <span class="gl-required">*</span></label>
            <textarea class="gl-textarea" v-model="removeForm.notes" rows="2" placeholder="Explain why this stock is being removed…"></textarea>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-secondary" @click="removeModalOpen = false">Cancel</button>
          <button class="gl-btn-warning" @click="removeStock" :disabled="submitting">
            <span v-if="submitting" class="gl-btn-spinner"></span>
            Remove Stock
          </button>
        </div>
      </div>
    </div>

    <!-- ── ADJUST STOCK MODAL ── -->
    <div v-if="adjustModalOpen" class="gl-modal-backdrop" @click.self="adjustModalOpen = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <div class="gl-modal-title-wrap">
            <div class="gl-modal-icon gl-modal-icon-adjust">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </div>
            <span class="gl-modal-title">Adjust Stock Count</span>
          </div>
          <button class="gl-modal-close" @click="adjustModalOpen = false">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-info-row">
            <div class="gl-info-product">{{ selectedProduct?.name }}</div>
            <div class="gl-info-meta">Current stock: <strong>{{ selectedProduct?.pivot?.stock || 0 }}</strong></div>
          </div>
          <div class="gl-form-group">
            <label class="gl-label">New Stock Count <span class="gl-required">*</span></label>
            <input type="number" class="gl-input" v-model="adjustForm.new_quantity" min="0">
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Reason <span class="gl-required">*</span></label>
            <textarea class="gl-textarea" v-model="adjustForm.reason" rows="2" placeholder="Explain why the stock is being adjusted…"></textarea>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-secondary" @click="adjustModalOpen = false">Cancel</button>
          <button class="gl-btn-info" @click="adjustStock" :disabled="submitting">
            <span v-if="submitting" class="gl-btn-spinner"></span>
            Adjust Stock
          </button>
        </div>
      </div>
    </div>

    <!-- ── ALERT SETTINGS MODAL ── -->
    <div v-if="alertModalOpen" class="gl-modal-backdrop" @click.self="alertModalOpen = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <div class="gl-modal-title-wrap">
            <div class="gl-modal-icon gl-modal-icon-alert">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            </div>
            <span class="gl-modal-title">Stock Alert Settings</span>
          </div>
          <button class="gl-modal-close" @click="alertModalOpen = false">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-info-row">
            <div class="gl-info-product">{{ selectedProduct?.name }}</div>
          </div>
          <div class="gl-form-row">
            <div class="gl-form-group">
              <label class="gl-label">Min Stock Level</label>
              <input type="number" class="gl-input" v-model="alertForm.min_stock" min="0">
              <span class="gl-hint">Alert when below this</span>
            </div>
            <div class="gl-form-group">
              <label class="gl-label">Reorder Point</label>
              <input type="number" class="gl-input" v-model="alertForm.reorder_point" min="0">
              <span class="gl-hint">Trigger reorder at this qty</span>
            </div>
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Max Stock (optional)</label>
            <input type="number" class="gl-input" v-model="alertForm.max_stock" min="0" placeholder="No limit">
          </div>
          <div class="gl-form-group">
            <div class="gl-toggle-row">
              <div>
                <div class="gl-label" style="margin:0">Enable Alerts</div>
                <div class="gl-hint">Notify when stock hits thresholds</div>
              </div>
              <button type="button" class="gl-toggle" :class="{ on: alertForm.alert_enabled }" @click="alertForm.alert_enabled = !alertForm.alert_enabled">
                <span class="gl-toggle-thumb"></span>
              </button>
            </div>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-secondary" @click="alertModalOpen = false">Cancel</button>
          <button class="gl-btn-primary" @click="saveAlertSettings" :disabled="submitting">
            <span v-if="submitting" class="gl-btn-spinner"></span>
            Save Settings
          </button>
        </div>
      </div>
    </div>

  </div>

  <!-- Mobile FAB — Add Stock -->
  <button v-if="selectedBranchId" class="gl-mobile-fab" @click="showAddModal()">
    <span class="gl-fab-inner">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      Add Stock
    </span>
  </button>

</template>

<script>
import { ownerStockApi, ownerBranchApi } from '@/services/ownerApi';

export default {
  name: 'StockList',

  data() {
    return {
      loading: false,
      submitting: false,
      selectedBranchId: '',
      selectedProduct: null,
      branches: [],
      products: [],
      summary: null,
      searchTimeout: null,
      imgErrors: {},
      filters: {
        search: '',
        stock_status: '',
        category_id: ''
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
      },
      stockForm: {
        product_id: '',
        quantity: 1,
        type: 'purchase',
        unit_cost: '',
        reference_number: '',
        notes: ''
      },
      removeForm: {
        quantity: 1,
        type: 'waste',
        notes: ''
      },
      adjustForm: {
        new_quantity: 0,
        reason: ''
      },
      alertForm: {
        min_stock: 0,
        reorder_point: 0,
        max_stock: null,
        alert_enabled: true
      },
      addModalOpen: false,
      removeModalOpen: false,
      adjustModalOpen: false,
      alertModalOpen: false,
    };
  },

  mounted() {
    this.fetchBranches();
    if (this.$route.params.branchId) {
      this.selectedBranchId = parseInt(this.$route.params.branchId);
      this.fetchStock();
    }
  },

  methods: {
    formatPrice(price) {
      return price ? parseFloat(price).toFixed(2) : '0.00';
    },

    isLowStock(product) {
      const stock = product.pivot?.stock || 0;
      const minStock = product.pivot?.min_stock || 0;
      return minStock > 0 && stock <= minStock && stock > 0;
    },

    isOutOfStock(product) {
      return (product.pivot?.stock || 0) === 0;
    },

    getStockBadgeClass(product) {
      if (this.isOutOfStock(product)) return 'gl-stock-out';
      if (this.isLowStock(product)) return 'gl-stock-low';
      return 'gl-stock-ok';
    },

    getStatusLabel(product) {
      if (this.isOutOfStock(product)) return 'Out of Stock';
      if (this.isLowStock(product)) return 'Low Stock';
      return 'In Stock';
    },

    getStatusClass(product) {
      if (this.isOutOfStock(product)) return 'gl-status-out';
      if (this.isLowStock(product)) return 'gl-status-low';
      return 'gl-status-ok';
    },

    debouncedSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => { this.fetchStock(); }, 300);
    },

    resetFilters() {
      this.filters.search = '';
      this.filters.stock_status = '';
      this.fetchStock();
    },

    onBranchChange() {
      this.products = [];
      this.summary = null;
      this.pagination = { current_page: 1, last_page: 1, per_page: 20, total: 0 };
      this.fetchStock();
    },

    async fetchBranches() {
      try {
        const response = await ownerBranchApi.list({ per_page: 100 });
        this.branches = response.data.data;
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }
    },

    async fetchStock(page = 1) {
      if (!this.selectedBranchId) return;
      try {
        this.loading = true;
        const response = await ownerStockApi.list(this.selectedBranchId, { page, ...this.filters });
        this.products = response.data.data;
        this.summary = response.data.summary;
        if (response.data.meta) {
          this.pagination = response.data.meta;
        }
      } catch (error) {
        console.error('Failed to fetch stock:', error);
      } finally {
        this.loading = false;
      }
    },

    showAddModal(product = null) {
      this.selectedProduct = product;
      this.stockForm = { product_id: product?.id || '', quantity: 1, type: 'purchase', unit_cost: '', reference_number: '', notes: '' };
      this.addModalOpen = true;
    },

    showRemoveModal(product) {
      this.selectedProduct = product;
      this.removeForm = { quantity: 1, type: 'waste', notes: '' };
      this.removeModalOpen = true;
    },

    showAdjustModal(product) {
      this.selectedProduct = product;
      this.adjustForm = { new_quantity: product.pivot?.stock || 0, reason: '' };
      this.adjustModalOpen = true;
    },

    showAlertSettings(product) {
      this.selectedProduct = product;
      this.alertForm = {
        min_stock: product.pivot?.min_stock || 0,
        reorder_point: product.pivot?.reorder_point || 0,
        max_stock: product.pivot?.max_stock || null,
        alert_enabled: product.pivot?.alert_enabled ?? true
      };
      this.alertModalOpen = true;
    },

    async addStock() {
      try {
        this.submitting = true;
        await ownerStockApi.addStock(this.selectedBranchId, this.stockForm);
        this.addModalOpen = false;
        this.fetchStock(this.pagination.current_page);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to add stock');
      } finally {
        this.submitting = false;
      }
    },

    async removeStock() {
      try {
        this.submitting = true;
        await ownerStockApi.removeStock(this.selectedBranchId, { product_id: this.selectedProduct.id, ...this.removeForm });
        this.removeModalOpen = false;
        this.fetchStock(this.pagination.current_page);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to remove stock');
      } finally {
        this.submitting = false;
      }
    },

    async adjustStock() {
      try {
        this.submitting = true;
        await ownerStockApi.adjustStock(this.selectedBranchId, { product_id: this.selectedProduct.id, ...this.adjustForm });
        this.adjustModalOpen = false;
        this.fetchStock(this.pagination.current_page);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to adjust stock');
      } finally {
        this.submitting = false;
      }
    },

    async saveAlertSettings() {
      try {
        this.submitting = true;
        await ownerStockApi.updateAlertSettings(this.selectedBranchId, this.selectedProduct.id, this.alertForm);
        this.alertModalOpen = false;
        this.fetchStock(this.pagination.current_page);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to save settings');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
/* ── Page layout ── */
.gl-branch-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-branch-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-branch-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }

.gl-header-actions { display: flex; align-items: center; gap: 10px; }

.gl-add-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; background: #900C27; color: #fff;
  border: none; border-radius: 10px; font-size: 13.5px; font-weight: 600;
  cursor: pointer; text-decoration: none;
  box-shadow: 0 2px 8px rgba(144,12,39,0.25);
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
}
.gl-add-btn:hover { background: #6f0a1e; color: #fff; text-decoration: none; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(144,12,39,0.30); }

.gl-outline-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px; border: 1.5px solid #e8eaf0; border-radius: 10px;
  font-size: 13px; font-weight: 600; color: #374151; background: #fff;
  text-decoration: none; cursor: pointer; transition: border-color 0.15s, background 0.15s;
}
.gl-outline-btn:hover { border-color: #d1d5db; background: #f9fafb; color: #111827; text-decoration: none; }

/* ── Filter bar ── */
.gl-filter-bar {
  display: flex; align-items: center; gap: 10px;
  background: transparent; border: none;
  padding: 0; margin-bottom: 14px; flex-wrap: wrap;
}
.gl-filter-bar-sep { width: 1px; height: 22px; background: #e8eaf0; margin: 0 2px; flex-shrink: 0; }

.gl-branch-select-wrap { position: relative; display: flex; align-items: center; flex-shrink: 0; }
.gl-branch-icon { position: absolute; left: 10px; color: #6b7280; pointer-events: none; z-index: 1; }
.gl-branch-chevron { position: absolute; right: 10px; color: #9ca3af; pointer-events: none; }
.gl-branch-select {
  appearance: none; -webkit-appearance: none;
  border: 1.5px solid #e8eaf0; border-radius: 9px;
  padding: 7px 32px 7px 30px; font-size: 13.5px; font-weight: 600;
  color: #111827; background: #f9fafb; cursor: pointer; outline: none;
  min-width: 180px; transition: border-color 0.15s;
}
.gl-branch-select:focus { border-color: #900C27; background: #fff; }

.gl-search-wrap { position: relative; flex: 1; min-width: 180px; }
.gl-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.gl-search-input { width: 100%; border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 7px 10px 7px 30px; font-size: 13.5px; color: #111827; outline: none; background: #fff; transition: border-color 0.15s; box-sizing: border-box; }
.gl-search-input:focus { border-color: #900C27; }
.gl-search-input::placeholder { color: #c0c4cc; }

.gl-filter-select { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 7px 12px; font-size: 13px; color: #374151; background: #fff; outline: none; cursor: pointer; transition: border-color 0.15s; }
.gl-filter-select:focus { border-color: #900C27; }

.gl-filter-clear { display: inline-flex; align-items: center; gap: 5px; padding: 6px 12px; border: 1.5px solid #fecaca; border-radius: 8px; background: #fff5f5; color: #dc2626; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
.gl-filter-clear:hover { background: #fee2e2; }

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center; gap: 0;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  padding: 12px 18px; margin-bottom: 16px; flex-wrap: wrap; gap: 4px;
}
.gl-stat-chip { display: flex; flex-direction: column; align-items: flex-start; padding: 4px 16px 4px 0; min-height: 38px; }
.gl-stat-num { font-size: 20px; font-weight: 700; color: #111827; line-height: 1.1; flex: 1; display: flex; align-items: flex-start; }
.gl-stat-lbl { font-size: 11px; color: #9ca3af; font-weight: 500; margin-top: auto; white-space: nowrap; }
.gl-stat-divider { width: 1px; height: 32px; background: #f0f1f3; margin: 0 12px; flex-shrink: 0; }
.gl-stat-warn { color: #d97706 !important; }
.gl-stat-danger { color: #dc2626 !important; }
.gl-stat-info { color: #0ea5e9 !important; }

/* ── Empty / Loading ── */
.gl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 80px 0; color: #9ca3af; text-align: center; }
.gl-empty p { margin: 0; font-size: 14px; }
.gl-loading { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 60px 0; font-size: 14px; color: #9ca3af; }
.gl-spinner { width: 20px; height: 20px; border: 2px solid #f0f1f3; border-top-color: #900C27; border-radius: 50%; animation: gl-spin 0.7s linear infinite; flex-shrink: 0; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Table card ── */
.gl-table-card { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px; overflow: hidden; margin-bottom: 24px; }
.gl-table-wrap { overflow-x: auto; }
.gl-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.gl-table thead tr { background: #f8f9fa; border-bottom: 1.5px solid #f0f1f3; }
.gl-table thead th { padding: 11px 16px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; white-space: nowrap; }
.gl-table thead th.th-center { text-align: center; }
.gl-table tbody tr { border-bottom: 1px solid #f5f6f8; transition: background 0.1s; }
.gl-table tbody tr:last-child { border-bottom: none; }
.gl-table tbody tr:hover { background: #fafafa; }
.gl-table tbody td { padding: 11px 16px; color: #374151; vertical-align: middle; }
.gl-table tbody td.td-center { text-align: center; }

/* Row states */
.gl-row-out { background: #fff5f5 !important; }
.gl-row-out:hover { background: #fff0f0 !important; }
.gl-row-low { background: #fffbf0 !important; }
.gl-row-low:hover { background: #fff8e6 !important; }

/* Product cell */
.gl-prod-cell { display: flex; align-items: center; gap: 10px; }
.gl-prod-img-sm { width: 40px; height: 40px; border-radius: 8px; overflow: hidden; flex-shrink: 0; background: #f3f4f6; display: flex; align-items: center; justify-content: center; }
.gl-prod-img-sm img { width: 100%; height: 100%; object-fit: cover; }
.gl-prod-placeholder { color: #d1d5db; }
.gl-prod-name { font-weight: 600; font-size: 13.5px; color: #111827; }
.gl-prod-sku { font-size: 11px; color: #9ca3af; margin-top: 1px; font-family: 'Courier New', monospace; letter-spacing: 0.02em; }

/* Category pill */
.gl-cat-pill { display: inline-block; background: #f3f4f6; color: #6b7280; font-size: 11.5px; padding: 3px 9px; border-radius: 20px; white-space: nowrap; }

/* Stock badge */
.gl-stock-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 34px; height: 24px; padding: 0 8px; border-radius: 6px; font-size: 12px; font-weight: 700; }
.gl-stock-ok { background: #dff0ec; color: #900C27; }
.gl-stock-low { background: #fef3c7; color: #d97706; }
.gl-stock-out { background: #fee2e2; color: #dc2626; }

/* Min stock */
.gl-min-stock { font-size: 13px; color: #6b7280; }

/* Status pill */
.gl-status-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
.gl-status-ok { background: #dff0ec; color: #900C27; }
.gl-status-low { background: #fef3c7; color: #d97706; }
.gl-status-out { background: #fee2e2; color: #dc2626; }

/* Action buttons */
.gl-action-group { display: flex; align-items: center; gap: 4px; justify-content: center; }
.gl-act-btn { width: 30px; height: 30px; border-radius: 7px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: opacity 0.15s, transform 0.1s; flex-shrink: 0; }
.gl-act-btn:hover { opacity: 0.82; transform: scale(1.07); }
.gl-act-add { background: #900C27; color: #fff; }
.gl-act-remove { background: #d97706; color: #fff; }
.gl-act-adjust { background: #e5e7eb; color: #374151; }
.gl-act-alert { background: transparent; border: 1.5px solid #e5e7eb !important; color: #6b7280; }

/* Pagination */
.gl-pagination { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 14px; border-top: 1px solid #f0f1f3; }
.gl-page-btn { min-width: 32px; height: 32px; padding: 0 8px; border-radius: 7px; border: 1.5px solid #e8eaf0; background: #fff; font-size: 13px; color: #374151; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.12s; }
.gl-page-btn:hover:not(:disabled) { border-color: #a7c4be; background: #f0f5f4; }
.gl-page-btn.active { background: #900C27; border-color: #900C27; color: #fff; font-weight: 700; }
.gl-page-btn:disabled { opacity: 0.38; cursor: not-allowed; }

/* ── Modals ── */
.gl-modal-backdrop { position: fixed; inset: 0; background: rgba(17,24,39,0.5); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 24px; }
.gl-modal { position: relative; background: #fff; border-radius: 16px; width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.18); }
.gl-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 22px 0; }
.gl-modal-title-wrap { display: flex; align-items: center; gap: 10px; }
.gl-modal-close { position: absolute; top: 14px; right: 14px; }
.gl-modal-icon { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-modal-icon-add,
.gl-modal-icon-remove,
.gl-modal-icon-adjust,
.gl-modal-icon-alert { background: #dff0ec; color: #900C27; }
.gl-modal-title { font-size: 15px; font-weight: 700; color: #111827; }
.gl-modal-close { width: 28px; height: 28px; border-radius: 7px; border: none; background: #f3f4f6; color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
.gl-modal-close:hover { background: #fee2e2; color: #dc2626; }
.gl-modal-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 14px; }
.gl-modal-footer { padding: 0 22px 20px; display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #f5f6f8; padding-top: 16px; margin-top: 2px; }

/* Form elements */
.gl-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.gl-form-group { display: flex; flex-direction: column; gap: 5px; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #374151; }
.gl-required { color: #dc2626; margin-left: 2px; }
.gl-hint { font-size: 11.5px; color: #9ca3af; }
.gl-input { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 8px 12px; font-size: 13.5px; color: #111827; outline: none; background: #fff; width: 100%; box-sizing: border-box; transition: border-color 0.15s, box-shadow 0.15s; }
.gl-input:focus { border-color: #900C27; box-shadow: 0 0 0 3px rgba(144,12,39,0.15); }
.gl-input:disabled { background: #f9fafb; color: #9ca3af; cursor: not-allowed; }
.gl-select { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 8px 12px; font-size: 13.5px; color: #111827; outline: none; background: #fff; width: 100%; box-sizing: border-box; transition: border-color 0.15s; }
.gl-select:focus { border-color: #900C27; box-shadow: 0 0 0 3px rgba(144,12,39,0.15); }
.gl-select:disabled { background: #f9fafb; color: #9ca3af; cursor: not-allowed; }
.gl-textarea { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 8px 12px; font-size: 13.5px; color: #111827; outline: none; background: #fff; width: 100%; box-sizing: border-box; resize: vertical; transition: border-color 0.15s; }
.gl-textarea:focus { border-color: #900C27; box-shadow: 0 0 0 3px rgba(144,12,39,0.15); }

/* Info row (product name + current stock display) */
.gl-info-row { background: #f8f9fb; border: 1.5px solid #f0f1f3; border-radius: 9px; padding: 11px 14px; }
.gl-info-product { font-size: 13.5px; font-weight: 600; color: #111827; }
.gl-info-meta { font-size: 12px; color: #6b7280; margin-top: 2px; }
.gl-info-meta strong { color: #374151; }

/* Toggle */
.gl-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.gl-toggle { width: 42px; height: 24px; border-radius: 12px; background: #e5e7eb; border: none; cursor: pointer; padding: 2px; display: flex; align-items: center; transition: background 0.2s; flex-shrink: 0; }
.gl-toggle.on { background: #900C27; }
.gl-toggle-thumb { width: 20px; height: 20px; background: #fff; border-radius: 50%; box-shadow: 0 1px 3px rgba(0,0,0,0.2); transition: transform 0.2s; }
.gl-toggle.on .gl-toggle-thumb { transform: translateX(18px); }

/* Modal buttons */
.gl-btn-secondary { padding: 8px 18px; border-radius: 9px; border: 1.5px solid #e8eaf0; background: #fff; color: #6b7280; font-size: 13.5px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.gl-btn-secondary:hover { background: #f3f4f6; }
.gl-btn-success { padding: 8px 18px; border-radius: 9px; border: none; background: #900C27; color: #fff; font-size: 13.5px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity 0.15s; }
.gl-btn-success:hover:not(:disabled) { opacity: 0.88; }
.gl-btn-success:disabled { opacity: 0.5; cursor: not-allowed; }
.gl-btn-warning { padding: 8px 18px; border-radius: 9px; border: none; background: #d97706; color: #fff; font-size: 13.5px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity 0.15s; }
.gl-btn-warning:hover:not(:disabled) { opacity: 0.88; }
.gl-btn-warning:disabled { opacity: 0.5; cursor: not-allowed; }
.gl-btn-info { padding: 8px 18px; border-radius: 9px; border: none; background: #900C27; color: #fff; font-size: 13.5px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity 0.15s; }
.gl-btn-info:hover:not(:disabled) { opacity: 0.88; }
.gl-btn-info:disabled { opacity: 0.5; cursor: not-allowed; }
.gl-btn-primary { padding: 8px 18px; border-radius: 9px; border: none; background: #900C27; color: #fff; font-size: 13.5px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity 0.15s; }
.gl-btn-primary:hover:not(:disabled) { opacity: 0.88; }
.gl-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.gl-btn-spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin 0.7s linear infinite; }

/* ── Mobile FAB ── */
.gl-mobile-fab {
  display: none;
  position: fixed; bottom: 24px; right: 20px; z-index: 200;
  background: transparent; border: none; padding: 0; cursor: pointer;
}
.gl-fab-inner {
  display: flex; align-items: center; gap: 8px;
  background: #900C27; color: #fff;
  padding: 13px 20px; border-radius: 50px;
  font-size: 14px; font-weight: 700;
  box-shadow: 0 6px 24px rgba(144,12,39,0.32), 0 2px 6px rgba(0,0,0,0.18);
  transition: transform 0.15s, box-shadow 0.15s;
}
.gl-mobile-fab:hover .gl-fab-inner { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(144,12,39,0.38), 0 3px 8px rgba(0,0,0,0.22); }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-stock-page { padding-bottom: 96px; }
  .gl-mobile-fab { display: block; }
  .gl-header-add-btn { display: none !important; }
  .gl-history-label { display: none; }

  /* Filter bar */
  .gl-filter-bar { flex-direction: column; align-items: stretch; gap: 8px; padding: 0; }
  .gl-filter-bar-sep { display: none; }
  .gl-branch-select-wrap { width: 100%; }
  .gl-branch-select { width: 100%; min-width: unset; }
  .gl-search-wrap { min-width: unset; }
  .gl-filter-select { width: 100%; }

  /* Stats strip */
  .gl-stats-strip { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 12px; }
  .gl-stat-divider { display: none; }
  .gl-stat-chip { padding: 10px 12px; background: #f8f9fb; border-radius: 10px; border: 1.5px solid #f0f1f3; height: 72px; box-sizing: border-box; }
  .gl-stat-num { font-size: 18px; }

  /* Table — hide Category + Min Stock columns */
  .gl-table thead th:nth-child(2),
  .gl-table tbody td:nth-child(2),
  .gl-table thead th:nth-child(4),
  .gl-table tbody td:nth-child(4) { display: none; }

  /* Bigger action buttons on mobile */
  .gl-act-btn { width: 34px; height: 34px; border-radius: 8px; }

  /* Modals */
  .gl-modal-backdrop { padding: 68px 16px 16px; align-items: center; }
  .gl-modal { border-radius: 16px; max-height: calc(100vh - 84px); }
  .gl-form-row { grid-template-columns: 1fr !important; }
}
</style>

<style>
/* Dark mode — non-scoped so body.dark-only selector works */
body.dark-only .gl-branch-title { color: #f3f4f6; }
body.dark-only .gl-branch-breadcrumb { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a:hover { color: #d1d5db; }

body.dark-only .gl-branch-select { background: #262932; border-color: #2d3249; color: #e5e7eb; }
body.dark-only .gl-branch-icon { color: #6b7280; }
body.dark-only .gl-search-input { background: #262932; border-color: #2d3249; color: #e5e7eb; }
body.dark-only .gl-search-input::placeholder { color: #4b5563; }
body.dark-only .gl-filter-select { background: #262932; border-color: #2d3249; color: #e5e7eb; }
body.dark-only .gl-outline-btn { background: #1e2130; border-color: #2d3249; color: #d1d5db; }
body.dark-only .gl-outline-btn:hover { background: #262932; color: #f3f4f6; }

body.dark-only .gl-stats-strip { background: #262932; border-color: #323644; }
body.dark-only .gl-stat-num { color: #f3f4f6; }
body.dark-only .gl-stat-divider { background: #323644; }

body.dark-only .gl-table-card { background: #1e2130; border-color: #2d3249; }
body.dark-only .gl-table thead tr { background: #252837; }
body.dark-only .gl-table thead th { color: #6b7280; }
body.dark-only .gl-table tbody tr { border-color: #2a2f46; }
body.dark-only .gl-table tbody tr:hover { background: #252837 !important; }
body.dark-only .gl-table tbody td { color: #e5e7eb; }
body.dark-only .gl-row-out { background: rgba(220,38,38,0.08) !important; }
body.dark-only .gl-row-low { background: rgba(217,119,6,0.08) !important; }
body.dark-only .gl-prod-name { color: #f3f4f6; }
body.dark-only .gl-prod-img-sm { background: #2d3249; }
body.dark-only .gl-cat-pill { background: #2d3249; color: #9ca3af; }
body.dark-only .gl-min-stock { color: #9ca3af; }
body.dark-only .gl-act-adjust { background: #2d3249; color: #9ca3af; }
body.dark-only .gl-act-alert { border-color: #2d3249 !important; color: #9ca3af; }

body.dark-only .gl-pagination { border-color: #2d3249; }
body.dark-only .gl-page-btn { border-color: #2d3249; background: #1e2130; color: #d1d5db; }
body.dark-only .gl-page-btn:hover:not(:disabled) { background: #1a2e1e; border-color: #166534; }

body.dark-only .gl-modal { background: #1e2130; }
body.dark-only .gl-modal-header { background: transparent !important; }
body.dark-only .gl-modal-icon-add,
body.dark-only .gl-modal-icon-remove,
body.dark-only .gl-modal-icon-adjust,
body.dark-only .gl-modal-icon-alert { background: rgba(144,12,39,0.25); }
body.dark-only .gl-modal-title { color: #f3f4f6; }
body.dark-only .gl-modal-close { background: #2d3249; color: #9ca3af; }
body.dark-only .gl-modal-close:hover { background: #3d1212; color: #f87171; }
body.dark-only .gl-modal-footer { border-color: #2d3249; }
body.dark-only .gl-label { color: #d1d5db; }
body.dark-only .gl-input { border-color: #2d3249; background: #262932; color: #e5e7eb; }
body.dark-only .gl-input:disabled { background: #1a1e2d; color: #6b7280; }
body.dark-only .gl-select { border-color: #2d3249; background: #262932; color: #e5e7eb; }
body.dark-only .gl-select:disabled { background: #1a1e2d; color: #6b7280; }
body.dark-only .gl-textarea { border-color: #2d3249; background: #262932; color: #e5e7eb; }
body.dark-only .gl-info-row { background: #262932; border-color: #2d3249; }
body.dark-only .gl-info-product { color: #f3f4f6; }
body.dark-only .gl-info-meta { color: #9ca3af; }
body.dark-only .gl-info-meta strong { color: #d1d5db; }
body.dark-only .gl-toggle { background: #2d3249; }
body.dark-only .gl-toggle.on { background: #900C27; }
body.dark-only .gl-btn-secondary { border-color: #2d3249; background: #262932; color: #9ca3af; }
body.dark-only .gl-btn-secondary:hover { background: #2d3249; }

/* FAB */
.gl-mobile-fab { background: transparent !important; border: none !important; box-shadow: none !important; }
body.dark-only .gl-mobile-fab { background: transparent !important; box-shadow: none !important; }
body.dark-only .gl-fab-inner { box-shadow: 0 6px 24px rgba(144,12,39,0.35), 0 2px 6px rgba(0,0,0,0.30) !important; }

/* Mobile dark stats strip chips */
@media (max-width: 767px) {
  body.dark-only .gl-stat-chip { background: #262932; border-color: #323644; }
}
</style>
